// @flow
// import-sort-ignore

import _ from 'lodash';
import { createSelector } from 'reselect';

import { daysFromNow } from '@zapier/date';
/* eslint-disable @zapier/zapier/disallow-incorrect-import-notation */
import {
  getCurrentPlan,
  intervalizePlans,
  isMonthly,
  removeUnusedLegacyPlans,
  toPlan,
} from '@zapier/billing/src/utils';

import type { ByNameProps, PlanIntervalTuple } from 'app/billing/types';
import * as ABTest from 'app/entities/ABTest';
import * as Account from 'app/entities/Account';
import * as Offer from 'app/entities/Offer';
import * as Plan from 'app/entities/Plan';
import * as Profile from 'app/entities/Profile';
import { getIntegerParam } from 'app/common/utils/RouterSelectors';
import { getYearlyDiscount } from 'app/billing/utils';
import { isEqualIgnoreCase } from 'app/common/CommonUtils';

import type { Currency } from '@zapier/common-utils';

/* eslint-enable @zapier/zapier/disallow-incorrect-import-notation */

type State = Object;

export const selectCardsOffset = (state: State) => state.billing.cardsOffset;

const hasBillingAccess = state => {
  const accountId =
    getIntegerParam('accountId', state) ||
    Account.selectors.all.currentAccountId(state);
  return Profile.selectors.all.isAdminOf(accountId, state);
};

/* Get the account object for current user */
const selectAccount = Account.selectors.all.currentAccount;

/* Get the billing object for current user / account */
const selectBilling = createSelector(selectAccount, account => account.billing);

/* Get the selected credit card for this user / account */
export const selectActiveCreditCard = createSelector(selectBilling, billing =>
  _.get(billing, ['active_card'])
);

/* Get account plans object, format { [planId]: accountPlanObject, } */
const selectAccountPlansObject = (state: State) =>
  Plan.selectors.all.entitiesDataById(state);

/* Convert accountPlans into an array of accountPlan objects */
export const selectAccountPlans = createSelector(
  selectAccountPlansObject,
  accountPlansObject => _.values(accountPlansObject)
);

export const selectPlans = createSelector(selectAccountPlans, intervalizePlans);

const findPlanByName = (plans: Array<Plan>, planName: string) =>
  _.find(plans, p => isEqualIgnoreCase(planName, p.name));

// $FlowFixMe - flow doesn't understand we are passing through props
export const selectPlanByName = createSelector(
  selectPlans,
  (state, props: ByNameProps) => props,
  (plans, { planName }: ByNameProps) => findPlanByName(plans, planName)
);

export const selectSelectedBillingIntervals = createSelector(
  (state: State) => state.billing.selectedBillingIntervals,
  selectedBillingIntervals => selectedBillingIntervals
);

// $FlowFixMe - flow doesn't understand we are passing through props
export const selectFirstPlanId = createSelector(
  selectPlans,
  (state, props: ByNameProps) => props,
  (plans, { planName }: ByNameProps) => {
    const plan = findPlanByName(plans, planName);
    return _.get(plan, ['intervals', '0', 'id']);
  }
);

// $FlowFixMe - flow doesn't understand we are passing through props
export const selectPlanId = createSelector(
  selectPlans,
  (state, props: PlanIntervalTuple) => props,
  (plans, { planName, intervalName }: PlanIntervalTuple) => {
    const plan = findPlanByName(plans, planName);
    const intervals = plan ? plan.intervals : [];
    const interval = _.find(intervals, i => i.interval === intervalName) || {};
    return interval.id;
  }
);

export const selectCard = (state: State) => state.billing.card;
export const selectIsInFlight = (state: State) => state.billing.isInFlight;
export const selectIsConfirmingPlanChange = (state: State) =>
  state.billing.isConfirmingPlanChange;
export const selectIsEditingCard = (state: State) =>
  state.billing.isEditingCard;
export const selectUnderstoodDowngrade = (state: State) =>
  state.billing.understandsDowngrade;
export const selectDowngradeQuestions = (state: State) =>
  state.billing.downgradeQuestions;
export const selectDowngradeComment = (state: State) =>
  state.billing.downgradeComment;
export const selectNewAccountId = (state: State) => state.billing.newAccountId;

export const selectDowngradeOfferToShow = (
  downgradeOffers: Array<*>,
  state: Object
) => {
  const offerToBeShown = _.find(downgradeOffers, offer => {
    const isEligible =
      Offer.selectors.all.byOfferCode(offer.offerCode, state) !== undefined &&
      (offer.isEligible || (() => true))(state);

    return isEligible;
  });

  if (!offerToBeShown) return null;

  return {
    ...offerToBeShown,
    shouldShowForUser: ABTest.selectors.all.shouldRunVariantForTest(
      offerToBeShown.experimentVariant,
      offerToBeShown.experimentName,
      state
    ),
  };
};

export const isEligibleForAnnualUpgrade = (state: State) => {
  if (hasBillingAccess(state)) {
    const currentPlanId = Account.selectors.all.currentPlanId(state);
    const consolidatedPlans = selectPlans(state);
    const filteredPlans = removeUnusedLegacyPlans(
      consolidatedPlans,
      currentPlanId
    );
    const plans = filteredPlans.map(plan => toPlan(plan)); // do not pass second index arg
    const currentPlan = getCurrentPlan(plans, currentPlanId);
    const isLoading =
      Account.selectors.all.isLoadImpending(state) ||
      Plan.selectors.all.isLoading(state) ||
      // It's possible some plans have loaded but not the current plan
      // and we need to wait until the current plan has loaded.
      !currentPlan;
    const currentAccount = Account.selectors.all.currentAccount(state);
    const firstKnownUpgradeDate = Account.selectors.firstKnownUpgradeDate(
      currentAccount
    );
    const currentPlanInterval = Plan.selectors.interval(
      Plan.selectors.all.entity(currentPlanId, state)
    );

    const isOnMonthlyInterval = isMonthly(currentPlanInterval);
    const accountAgeDays = daysFromNow(firstKnownUpgradeDate);
    const yearlyDiscount = isLoading ? 0 : getYearlyDiscount(currentPlan);

    return isOnMonthlyInterval && yearlyDiscount > 0 && accountAgeDays < 45;
  }
  // User doesn't have billing access so don't show them anything about upgrading
  // account since they can't
  return false;
};

export const selectYearlyDiscountForCurrentPlan = (state: State) => {
  const currentPlanId = Account.selectors.all.currentPlanId(state);
  const consolidatedPlans = selectPlans(state);
  const filteredPlans = removeUnusedLegacyPlans(
    consolidatedPlans,
    currentPlanId
  );
  const plans = filteredPlans.map(plan => toPlan(plan)); // do not pass second index arg
  const currentPlan = getCurrentPlan(plans, currentPlanId);
  return getYearlyDiscount(currentPlan);
};

export const selectCurrentCurrency = ({ billing }: State) =>
  billing.currencies.selected;

const selectCurrencyData = (state: State): { [string]: Currency } =>
  state.billing.currencies.data;

export const selectCurrencies = createSelector(selectCurrencyData, data => {
  return Object.values(data).map(currency => ({
    ...currency,
    // $FlowFixMe: cannot cast currency since it's part of a map
    value: currency.name,
  }));
});

export const selectChangePlanError = ({ billing }: State) =>
  billing.changePlanError;
